import {
  AfterViewInit,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { EffectRef } from '@angular/core';
import { SANDBOXED_WEB_COMPONENT_IFRAME_SRC } from './sandboxed-web-component-iframe-src.provider';

/**
 * Angular component that wraps a web component definition and mounts it to the view via an iframe
 * for sandboxing.
 */
@Component({
  standalone: true,
  selector: 'sandboxed-web-component',
  template: `
    <iframe
      #iframe
      [src]="iframeSrc"
      sandbox="allow-scripts allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      [style.width]="normalizeCssSize(width())"
      [height]="autoHeight() ? null : height()"
      [style.height]="autoHeight() ? iframeHeight() : normalizeCssSize(height())"
      style="border: none; display: block"
    ></iframe>
  `,
})
export class SandboxedWebComponent implements AfterViewInit, OnDestroy {
  /** Iframe width. */
  readonly width = input('100%');

  /** Iframe height. */
  readonly height = input('300');

  /** Enables auto-resizing the iframe to fit web component content. */
  readonly autoHeight = input(true);

  /** Data passed to the component. */
  readonly data = input();

  /** URL to the ES module with the web component class. */
  readonly src = input.required();

  /**
   * Name of the ES module export for the web component class. If omitted, the default export is
   * used. Not reactive.
   */
  readonly exportName = input();

  protected readonly iframeSrc = inject(SANDBOXED_WEB_COMPONENT_IFRAME_SRC);

  private readonly injector = inject(Injector);

  protected readonly iframeHeight = signal('300px');

  protected readonly iframe = viewChild.required<ElementRef<HTMLIFrameElement>>('iframe');

  private hasIframeLoaded = false;

  private postDataEffect?: EffectRef;

  private readonly onMessage = (event: MessageEvent) => {
    if (!this.autoHeight()) {
      return;
    }

    const iframe = this.iframe().nativeElement;
    if (event.source !== iframe.contentWindow) {
      return;
    }

    if (event.data?.type !== 'component-height') {
      return;
    }

    const nextHeight = Number(event.data?.height);
    if (!Number.isFinite(nextHeight) || nextHeight <= 0) {
      return;
    }

    this.iframeHeight.set(`${Math.ceil(nextHeight)}px`);
  };

  private readonly onIframeLoad = () => {
    this.hasIframeLoaded = true;
    this.postComponentData();
  };

  private postComponentData() {
    if (!this.hasIframeLoaded) {
      return;
    }

    const iframe = this.iframe().nativeElement;
    iframe.contentWindow?.postMessage(
      {
        type: 'component-data',
        data: this.data(),
        exportName: this.exportName(),
        src: this.src(),
      },
      '*',
    );
  }

  ngAfterViewInit() {
    const iframe = this.iframe().nativeElement;

    this.iframeHeight.set(this.normalizeCssSize(this.height()));

    iframe.addEventListener('load', this.onIframeLoad);
    window.addEventListener('message', this.onMessage);

    this.postDataEffect = effect(
      () => {
        this.data();
        this.src();
        this.exportName();
        this.postComponentData();
      },
      { injector: this.injector },
    );
  }

  ngOnDestroy() {
    const iframe = this.iframe()?.nativeElement;
    if (iframe) {
      iframe.removeEventListener('load', this.onIframeLoad);
    }

    this.postDataEffect?.destroy();
    window.removeEventListener('message', this.onMessage);
  }

  protected normalizeCssSize(value: string): string {
    return /^\d+$/.test(value) ? `${value}px` : value;
  }
}
